import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Tooltip,
  TextField,
  InputAdornment,
  Stack,
  Avatar,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CheckIcon from '@mui/icons-material/Check'
import CancelIcon from '@mui/icons-material/Cancel'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EmailIcon from '@mui/icons-material/Email'
import AssignmentIcon from '@mui/icons-material/Assignment'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'

import { NewOrganization, NewOrganizationService } from '../../services/NewOrganization/NewOrganizationService'
import { NotificationContext } from '../../providers/NotificationContext'
import HospitalFormDialog from '../Hospitals/shared/HospitalFormDialog.component'
import AgencyFormDialog from '../Agencies/shared/AgencyFormDialog.component'
import { Hospital } from '../../models/Hospital.model'
import { Agency } from '../../models/Agency.model'
import { HospitalUpdateEvent } from '../../events/HospitalUpdateEvent'
import { AgencyUpdateEvent } from '../../events/AgencyUpdateEvent'
import { SnackbarContext } from '../../providers/SnackbarContext'
import { ClaimStatusTypes } from '../../models/ClaimStatus.model'
import { USStates } from '../../models/USStates.model'

const PENDING_CONFIG = {
  label: 'Pending',
  color: '#FF9800',
  gradient: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
  icon: <HourglassEmptyIcon />,
  bgLight: '#FFF3E0',
}

const NewOrganizationRequests = (): JSX.Element => {
  const location = useLocation()
  const navigate = useNavigate()
  const isAgency = location.pathname.includes('agency')
  const { refreshNotifications } = useContext(NotificationContext)

  const [organizations, setOrganizations] = useState<NewOrganization[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Form dialog states
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false)
  const [selectedOrg, setSelectedOrg] = useState<NewOrganization | null>(null)
  const [actionLoading, setActionLoading] = useState<{ orgId: number; action: 'approve' | 'reject' } | null>(null)

  const entityLabel = isAgency ? 'Agency' : 'Hospital'

  // Fetch pending organizations
  const fetchOrganizations = async () => {
    setLoading(true)
    try {
      const service = new NewOrganizationService()
      const data = await service.getNewOrganizations(isAgency)
      setOrganizations(data)
    } catch (error) {
      console.error('Failed to fetch organizations:', error)
      setSnackbar({ open: true, message: 'Failed to load requests', severity: 'error' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrganizations()
    refreshNotifications()
    setSearchQuery('')
  }, [isAgency])

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })

  // Open modal to review/edit (only when clicking the card)
  const handleOpenForm = (org: NewOrganization) => {
    setSelectedOrg(org)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setSelectedOrg(null)
    setIsFormOpen(false)
  }

  // Direct approve action (no modal)
  const handleApprove = async (org: NewOrganization, event: React.MouseEvent) => {
    event.stopPropagation()
    setActionLoading({ orgId: org.id, action: 'approve' })
    try {
      const service = new NewOrganizationService()
      await service.approveOrganization(org.id, isAgency)
      SnackbarContext.show('Request approved successfully!')
      await fetchOrganizations()
      await refreshNotifications()
    } catch (error) {
      console.error(error)
      SnackbarContext.show('Failed to approve request', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  // Direct reject action (no modal)
  const handleReject = async (org: NewOrganization, event: React.MouseEvent) => {
    event.stopPropagation()
    setActionLoading({ orgId: org.id, action: 'reject' })
    try {
      const service = new NewOrganizationService()
      await service.rejectOrganization(org.id, isAgency)
      SnackbarContext.show('Request rejected successfully!')
      await fetchOrganizations()
      await refreshNotifications()
    } catch (error) {
      console.error(error)
      SnackbarContext.show('Failed to reject request', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  // Convert NewOrganization to Hospital format for the form
  const convertToHospital = (org: NewOrganization): Hospital => {
    return {
      id: org.id,
      name: org.name,
      city: org.city,
      state: org.state as USStates,
      address: org.address,
      createdAt: org.createdAt,
      updatedAt: org.updatedAt,
      permalink: '',
      imageURL: null,
      latitude: null,
      longitude: null,
      zipcode: null,
      website: null,
      supportTelephone: null,
      supportEmail: null,
      submissionNotes: org.submissionNotes ?? null,
      submittedByEmail: org.submittedByEmail ?? null,
      externalId: null,
      averageRating: 0,
      reviewCount: 0,
      stableId: null,
      facilityCode: null,
      claimStatus: ClaimStatusTypes.UNCLAIMED,
    }
  }

  // Convert NewOrganization to Agency format for the form
  const convertToAgency = (org: NewOrganization): Agency => {
    return {
      id: org.id,
      name: org.name,
      address: org.address,
      createdAt: org.createdAt,
      updatedAt: org.updatedAt,
      permalink: '',
      imageURL: null,
      website: null,
      phone: null,
      email: null,
      submissionNotes: org.submissionNotes ?? null,
      submittedByEmail: org.submittedByEmail ?? null,
      externalId: null,
      facebook: null,
      averageRating: 0,
      reviewCount: 0,
      claimStatus: ClaimStatusTypes.UNCLAIMED,
    }
  }

  // Handle form submission for Hospital (when editing from modal)
  const handleHospitalFormSubmit = async (
    changedProperties: HospitalUpdateEvent,
    setIsFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!selectedOrg) return

    try {
      const service = new NewOrganizationService()

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: _id, ...changes } = changedProperties
      const hasChanges = Object.keys(changes).length > 0

      await service.approveOrganization(selectedOrg.id, isAgency, hasChanges ? changes : undefined)
      SnackbarContext.show('Request approved successfully!')

      handleCloseForm()
      await fetchOrganizations()
      await refreshNotifications()
    } catch (error) {
      console.error(error)
      SnackbarContext.show('Failed to approve request', 'error')
      setIsFormSubmitting(false)
    }
  }

  // Handle form submission for Agency (when editing from modal)
  const handleAgencyFormSubmit = async (
    changedProperties: AgencyUpdateEvent,
    setIsFormSubmitting: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (!selectedOrg) return

    try {
      const service = new NewOrganizationService()

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: _id, ...changes } = changedProperties
      const hasChanges = Object.keys(changes).length > 0

      await service.approveOrganization(selectedOrg.id, isAgency, hasChanges ? changes : undefined)
      SnackbarContext.show('Request approved successfully!')

      handleCloseForm()
      await fetchOrganizations()
      await refreshNotifications()
    } catch (error) {
      console.error(error)
      SnackbarContext.show('Failed to approve request', 'error')
      setIsFormSubmitting(false)
    }
  }

  const matchesSearch = (org: NewOrganization, query: string): boolean => {
    if (!query.trim()) return true
    const lowerQuery = query.toLowerCase()
    if (org.id.toString().includes(lowerQuery)) return true
    if (org.name?.toLowerCase().includes(lowerQuery)) return true
    if (org.city?.toLowerCase().includes(lowerQuery)) return true
    if (org.state?.toLowerCase().includes(lowerQuery)) return true
    if (org.address?.toLowerCase().includes(lowerQuery)) return true
    if (org.submittedByEmail?.toLowerCase().includes(lowerQuery)) return true
    if (org.submissionNotes?.toLowerCase().includes(lowerQuery)) return true
    return false
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const filteredOrganizations = searchQuery.trim()
    ? organizations.filter((org) => matchesSearch(org, searchQuery))
    : organizations

  return (
    <Box sx={{ pb: 4 }}>
      {/* ════════════ Hero Banner ════════════ */}
      <Box
        sx={{
          mb: 4,
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          background: isAgency
            ? 'linear-gradient(135deg, #00695C 0%, #26A69A 50%, #4DB6AC 100%)'
            : 'linear-gradient(135deg, #007C91 0%, #00ACC1 50%, #26C6DA 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', right: -30, top: -30, opacity: 0.07 }}>
          <AssignmentIcon sx={{ fontSize: 240 }} />
        </Box>
        <Box sx={{ position: 'relative' }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
              New {entityLabel} Requests
            </Typography>
            <Chip
              label={entityLabel}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 700,
                backdropFilter: 'blur(8px)',
              }}
            />
          </Stack>
          <Typography variant="body1" sx={{ opacity: 0.9, mb: 2, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
            Review and approve new {entityLabel.toLowerCase()} submissions
          </Typography>
          <Stack direction="row" spacing={{ xs: 2, sm: 3 }} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <AssignmentIcon sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {organizations.length} Total Requests
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <TrendingUpIcon sx={{ fontSize: 20, opacity: 0.8 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {organizations.length} Awaiting Review
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* ════════════ Pending Stat Card ════════════ */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card
            sx={{
              cursor: 'default',
              borderRadius: { xs: 2.5, sm: 3 },
              border: `2px solid ${PENDING_CONFIG.color}`,
              boxShadow: `0 4px 20px ${PENDING_CONFIG.color}25`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <Box sx={{ height: 4, background: PENDING_CONFIG.gradient }} />
            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, '&:last-child': { pb: { xs: 1.5, sm: 2.5 } } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: PENDING_CONFIG.color,
                      textTransform: 'uppercase',
                      letterSpacing: 0.8,
                      fontSize: { xs: '0.55rem', sm: '0.75rem' },
                    }}
                  >
                    {PENDING_CONFIG.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: '#1A2027',
                      lineHeight: 1.1,
                      mt: 0.5,
                      fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
                    }}
                  >
                    {loading ? '—' : organizations.length}
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    width: { xs: 36, sm: 48 },
                    height: { xs: 36, sm: 48 },
                    bgcolor: PENDING_CONFIG.color,
                    color: 'white',
                    display: { xs: 'none', sm: 'flex' },
                  }}
                >
                  {React.cloneElement(PENDING_CONFIG.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                </Avatar>
              </Stack>
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: PENDING_CONFIG.color,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                  Awaiting review
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ════════════ Search Bar ════════════ */}
      <TextField
        fullWidth
        placeholder={`Search ${entityLabel.toLowerCase()} requests by name, city, state, or ID…`}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.disabled' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2.5,
            bgcolor: '#FFFFFF',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            '& fieldset': { borderColor: 'rgba(0,124,145,0.12)' },
            '&:hover fieldset': { borderColor: 'rgba(0,124,145,0.3)' },
            '&.Mui-focused fieldset': { borderColor: '#007C91' },
          },
        }}
      />

      {/* ════════════ Results Header ════════════ */}
      {!loading && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2, px: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 700, color: '#263238', fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              Pending Requests
            </Typography>
            <Chip
              label={`${filteredOrganizations.length} result${filteredOrganizations.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ bgcolor: `${PENDING_CONFIG.color}18`, color: PENDING_CONFIG.color, fontWeight: 700, fontSize: '0.75rem' }}
            />
          </Stack>
        </Stack>
      )}

      {/* ════════════ Requests List ════════════ */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress sx={{ color: PENDING_CONFIG.color }} />
        </Box>
      ) : filteredOrganizations.length === 0 ? (
        <Box
          sx={{
            py: 8,
            px: 4,
            textAlign: 'center',
            background: 'linear-gradient(180deg, #FAFBFC 0%, #F0F4F8 100%)',
            borderRadius: 4,
            border: '2px dashed rgba(0,0,0,0.08)',
          }}
        >
          <Avatar
            sx={{
              width: 72,
              height: 72,
              bgcolor: PENDING_CONFIG.bgLight,
              color: PENDING_CONFIG.color,
              mx: 'auto',
              mb: 2.5,
            }}
          >
            {React.cloneElement(PENDING_CONFIG.icon, { sx: { fontSize: 36 } })}
          </Avatar>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#263238', mb: 1 }}>
            {searchQuery.trim() ? 'No Matching Requests' : 'No Pending Requests'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#78909C', maxWidth: 420, mx: 'auto' }}>
            {searchQuery.trim()
              ? `No requests matching "${searchQuery.trim()}" were found.`
              : `There are currently no pending ${entityLabel.toLowerCase()} requests to review.`}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredOrganizations.map((org) => (
            <Grid item xs={12} sm={6} key={org.id}>
              <Card
                onClick={() => handleOpenForm(org)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: '1px solid rgba(0,0,0,0.06)',
                  borderLeft: `4px solid ${PENDING_CONFIG.color}`,
                  boxShadow: '0 1px 6px rgba(0,0,0,0.04)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  overflow: 'hidden',
                  position: 'relative',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: `0 6px 20px ${PENDING_CONFIG.color}18`,
                    '& .card-actions': { opacity: 1 },
                  },
                }}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    {/* Left: Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Name */}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: '#1A2027',
                          lineHeight: 1.3,
                          mb: 0.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {org.name || 'Unnamed'}
                      </Typography>

                      {/* Location */}
                      {(org.city || org.state || org.address) && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: org.submittedByEmail ? 0.75 : 1.5 }}>
                          <LocationOnIcon sx={{ fontSize: 15, color: '#90A4AE' }} />
                          <Typography variant="body2" sx={{ color: '#78909C' }}>
                            {[org.address, org.city, org.state].filter(Boolean).join(', ')}
                          </Typography>
                        </Stack>
                      )}

                      {org.submittedByEmail && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: org.submissionNotes ? 0.75 : 1.5 }}>
                          <EmailIcon sx={{ fontSize: 15, color: '#90A4AE' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#5C6BC0',
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {org.submittedByEmail}
                          </Typography>
                        </Stack>
                      )}

                      {org.submissionNotes ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#78909C',
                            mb: 1.5,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {org.submissionNotes}
                        </Typography>
                      ) : null}

                      {/* Bottom meta */}
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={`#${org.id}`}
                          sx={{
                            bgcolor: '#F5F5F5',
                            color: '#78909C',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 22,
                            '& .MuiChip-icon': { color: '#B0BEC5' },
                          }}
                        />
                        <Chip
                          size="small"
                          label={entityLabel}
                          sx={{
                            bgcolor: isAgency ? '#E0F7FA' : '#E0F2F1',
                            color: isAgency ? '#00838F' : '#00695C',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            height: 24,
                          }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <CalendarTodayIcon sx={{ fontSize: 13, color: '#B0BEC5' }} />
                          <Typography variant="caption" sx={{ color: '#90A4AE', fontSize: '0.72rem' }}>
                            {formatDate(org.createdAt)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>

                    {/* Right: Action Buttons */}
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <Stack
                        className="card-actions"
                        direction="row"
                        spacing={0.75}
                        sx={{ opacity: { xs: 1, sm: 0 }, transition: 'opacity 0.25s ease' }}
                      >
                        <Tooltip title="Reject" arrow>
                          <IconButton
                            size="small"
                            disabled={actionLoading?.orgId === org.id}
                            onClick={(e) => handleReject(org, e)}
                            sx={{
                              bgcolor: '#FFEBEE',
                              color: '#FF1744',
                              width: 36,
                              height: 36,
                              '&:hover': { bgcolor: '#FF1744', color: 'white' },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            {actionLoading?.orgId === org.id && actionLoading?.action === 'reject' ? (
                              <CircularProgress size={16} sx={{ color: '#FF1744' }} />
                            ) : (
                              <CancelIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve" arrow>
                          <IconButton
                            size="small"
                            disabled={actionLoading?.orgId === org.id}
                            onClick={(e) => handleApprove(org, e)}
                            sx={{
                              bgcolor: '#E8F5E9',
                              color: '#00C853',
                              width: 36,
                              height: 36,
                              '&:hover': { bgcolor: '#00C853', color: 'white' },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            {actionLoading?.orgId === org.id && actionLoading?.action === 'approve' ? (
                              <CircularProgress size={16} sx={{ color: '#00C853' }} />
                            ) : (
                              <CheckIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modal - Only opens when clicking the card to review/edit before approving */}
      {selectedOrg && (
        <>
          {isAgency ? (
            <AgencyFormDialog
              open={isFormOpen}
              agency={convertToAgency(selectedOrg)}
              onEdit={handleAgencyFormSubmit}
              onCancel={handleCloseForm}
              onClose={handleCloseForm}
            />
          ) : (
            <HospitalFormDialog
              open={isFormOpen}
              hospital={convertToHospital(selectedOrg)}
              onEdit={handleHospitalFormSubmit}
              onCancel={handleCloseForm}
              onClose={handleCloseForm}
            />
          )}
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={handleCloseSnackbar} sx={{ borderRadius: 2, fontWeight: 500 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default NewOrganizationRequests
